import React, { useState, useEffect } from 'react';
import { Plus, FileText } from 'lucide-react';
import { Card, Button, Spinner } from '../design-system';
import { useAuth } from '../../contexts/AuthContext';
import notesService from '../../services/notesService';
import NotesList from './NotesList';
import NoteEditorModal from './NoteEditorModal';
import toast from 'react-hot-toast';

const NotesSection = ({ patient }) => {
  const { user, userInfo } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [tagSuggestions, setTagSuggestions] = useState([]);

  // Fetch notes
  const fetchNotes = async () => {
    if (!patient?.patientId || !user) return;

    try {
      setLoading(true);
      const response = await notesService.getNotes(user, patient.patientId);
      setNotes(response.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Fetch tag suggestions
  const fetchTagSuggestions = async () => {
    if (!user) return;

    try {
      const response = await notesService.getTherapistTags(user);
      setTagSuggestions(response.tags || []);
    } catch (error) {
      console.error('Error fetching tag suggestions:', error);
      // Don't show error toast for tag suggestions - it's not critical
    }
  };

  useEffect(() => {
    if (patient?.patientId) {
      fetchNotes();
      fetchTagSuggestions();
    }
  }, [patient?.patientId]);

  const handleCreateNew = () => {
    setEditingNote(null);
    setShowEditor(true);
  };

  const handleNoteClick = async (note) => {
    try {
      // Fetch full note with content
      const response = await notesService.getNote(user, note.noteId);
      setEditingNote(response.note);
      setShowEditor(true);
    } catch (error) {
      console.error('Error fetching note:', error);
      toast.error('Failed to load note');
    }
  };

  const handleSave = async (noteData) => {
    if (!patient?.patientId || !user) return;

    try {
      setSaving(true);

      // Validate note data
      notesService.validateNoteData(noteData);

      if (editingNote) {
        // Update existing note
        await notesService.updateNote(user, editingNote.noteId, noteData);
        toast.success('Note updated successfully');
      } else {
        // Create new note
        await notesService.createNote(user, patient.patientId, noteData);
        toast.success('Note created successfully');
      }

      // Refresh notes list
      await fetchNotes();
      // Refresh tag suggestions (new tags might have been added)
      await fetchTagSuggestions();

      // Close editor
      setShowEditor(false);
      setEditingNote(null);
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(error.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditor = () => {
    if (!saving) {
      setShowEditor(false);
      setEditingNote(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card variant="default" padding="large">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{backgroundColor: 'rgba(44, 166, 164, 0.1)'}}>
              <FileText className="h-5 w-5" style={{color: 'var(--color-primary)'}} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
              <p className="text-sm text-gray-600">
                {loading ? 'Loading...' : `${notes.length} note${notes.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <Button
            onClick={handleCreateNew}
            variant="primary"
            size="medium"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            New Note
          </Button>
        </div>
      </Card>

      {/* Notes List */}
      <NotesList
        notes={notes}
        loading={loading}
        onNoteClick={handleNoteClick}
        onCreateNew={handleCreateNew}
      />

      {/* Editor Modal */}
      {showEditor && (
        <NoteEditorModal
          isOpen={showEditor}
          onClose={handleCloseEditor}
          onSave={handleSave}
          note={editingNote}
          tagSuggestions={tagSuggestions}
          saving={saving}
        />
      )}
    </div>
  );
};

export default NotesSection;

